// @ts-check
import {
  ComplianceState,
  DispatchEvents,
  ErrorState,
  HideState,
  JotType,
  WidgetOverrideParams,
  WidgetState,
} from "consts";
import {
  containsSelector,
  demographicsStorage,
  dispatchEvent,
  evalNatures,
  getCanonicalUrl,
  getConfigUrl,
  getLocalBackupAlias,
  getTopmostPageUrl,
  handleError,
  isNullOrUndef,
  normalizeUrl,
  toObject,
  widgetCompliance,
} from "utils";

import { envConfig } from "config";
import fetchJsonp from "fetch-jsonp";
import { jot } from "actions/jot";

/**
 * @typedef {import('../types/api-responses').BootstrapResponse} BootstrapResponse
 * @typedef {import('../types/api-responses').TargetResponse} TargetResponse
 * @typedef {import('../types/store').Store} Store
 * @typedef {import('../types/store').WidgetState} WidgetState
 */

const { widgetTargetEndpoint } = envConfig;

/**
 * Logs target resolution event
 * @param {number} oid - Original target ID
 * @param {number} targetId - Resolved target ID
 * @param {string | null} pin - Pinned question ID
 * @param {string | null} pinMode - Pin mode
 * @param {boolean} isMeta - Whether this is a meta-target
 * @param {Object} state - Current widget state
 */
const jotTargetResolved = (oid, targetId, pin, pinMode, isMeta, state) => {
  // Attempt to get article published time from meta tag
  const metaTag = document.querySelector('meta[property="article:published_time"]');
  const pubTime = metaTag ? metaTag.getAttribute("content") : null;
  const canonRaw = getCanonicalUrl();
  const canon = canonRaw ? normalizeUrl(canonRaw) : null;
  jot({
    state,
    space: "poll",
    type: JotType.Resolved,
    data: {
      otarget: oid,
      target: targetId,
      pin,
      pinMode,
      isMeta,
      pubTime,
      ...(canon && { canon }),
    },
  });
};

/**
 * Fetches and processes target configuration
 *
 * Loads target configuration from the API and determines widget state based on:
 * - Bootstrap contextual settings (question/target hidden states)
 * - User admin status (editable flag)
 * - DOM blacklist selectors
 * - Compliance state
 *
 * @param {Object} params
 * @param {Store} params.store - Unistore store instance
 * @param {WidgetState} params.state - Current widget state
 * @param {number} params.targetId - Target ID to fetch
 * @param {string} params.instanceId - Widget instance ID
 * @returns {Promise<Partial<WidgetState>>} Promise resolving to target configuration and calculated states, or error state
 */
export const getTarget = ({ store, state, targetId, instanceId }) => {
  const topmostPageUrl = getTopmostPageUrl(state.articleUrl || undefined);
  const urlContext = normalizeUrl(topmostPageUrl);
  const toState = {
    instanceId,
    config: {},
    complianceState: ComplianceState.Off,
    widgetState: WidgetState.TargetLoading,
    adEngageEnabled: false,
    readOnly: false,
    urlContext,
    questionsData: {},
    submitIds: [],
    servedIds: [],
  };
  store.setState(toState);
  const { apiTimeoutMS } = envConfig;
  const { bootstrap, hostname } = state;
  const {
    e: editable,
    contextualQuestionHiddenState,
    contextualTargetHiddenState,
    csAlias,
    compliance,
  } = bootstrap || {};

  const overrideNatures = (() => {
    const natures = topmostPageUrl.match(new RegExp(`[?&]${WidgetOverrideParams.Natures}=([^&]*)`));
    return isNullOrUndef(natures) ? null : decodeURIComponent(natures[1]).split(",");
  })();
  const readOnly = topmostPageUrl.includes(`${WidgetOverrideParams.Readonly}=1`);

  const canonRaw = getCanonicalUrl();
  const canon = canonRaw ? normalizeUrl(canonRaw) : null;
  return fetchJsonp(
    getConfigUrl(
      state,
      targetId,
      instanceId,
      widgetTargetEndpoint,
      urlContext,
      topmostPageUrl,
      csAlias,
      compliance,
      canon,
    ),
    { timeout: apiTimeoutMS },
  )
    .then((res) => res.json())
    .then((config) => {
      let widgetState = WidgetState.TargetLoaded;
      const {
        natures,
        rootbeer,
        compliance,
        domBlacklist: blacklistSelector,
        oid,
        id: targetId,
        contextualQuestion: pin,
        pinMode,
        isMeta,
        d,
      } = toObject(config);

      demographicsStorage.mergeLocalStorageDemographics(d);
      const updatedConfig = Object.assign({}, config, {
        natures: isNullOrUndef(overrideNatures) ? natures : overrideNatures,
      });
      const { adEngageEnabled: adEngageEnabledNature } = evalNatures(updatedConfig["natures"]);
      const adEngageEnabled = adEngageEnabledNature && rootbeer && typeof rootbeer === "object";
      const complianceCheckOn = widgetCompliance.complianceCheckOn(compliance);
      const complianceAccepted = widgetCompliance.complianceStorageOk(compliance);
      const complianceState = complianceCheckOn
        ? complianceAccepted
          ? ComplianceState.Accepted
          : ComplianceState.Ask
        : ComplianceState.Off;

      if (complianceAccepted || !complianceCheckOn) {
        dispatchEvent(instanceId, this, DispatchEvents.IDSync, false, {
          hostname,
        });
      }

      // set backupAlias which is our localStorage fallback identifier for a user
      // we set this independent of compliance state
      const backupAlias = getLocalBackupAlias();

      if (containsSelector(document, blacklistSelector)) {
        //doc contains blacklist selector, hide for respondents, show for admins
        widgetState = editable ? WidgetState.BlacklistAdmin : WidgetState.Hidden;
      } else if (contextualQuestionHiddenState === HideState.Respondent) {
        //ghost widget
        widgetState = WidgetState.Hidden;
      } else if (
        contextualTargetHiddenState === HideState.Admin ||
        contextualTargetHiddenState === HideState.AdminPosition
      ) {
        widgetState = WidgetState.TargetAdmin;
      } else if (contextualQuestionHiddenState === HideState.Admin) {
        widgetState = WidgetState.QuestionAdmin;
      }

      //jot target loaded if we are a normal target, or a metatarget that has
      //resolved to another target id
      if (!isMeta || oid !== targetId) {
        // must use latest state
        jotTargetResolved(oid, targetId, pin, pinMode, isMeta, store.getState());
      }

      /** @type {Partial<WidgetState>} */
      const result = {
        config: updatedConfig,
        urlContext,
        complianceState,
        widgetState,
        backupAlias,
        adEngageEnabled: !!adEngageEnabled,
        readOnly,
      };
      return result;
    })
    .catch((e) => handleError(ErrorState.Target, e));
};
